import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Languages } from "lucide-react";
import {
  SUPPORTED_LOCALES,
  resolveLocale,
  supportedLanguageLabel,
  type SupportedLocale,
} from "@/lib/locales";
import { cn } from "@/lib/utils";
import { useCloseOnOutsideAndEscape } from "@/components/ui/overlay";
import Label from "@/components/ui/Label";
import { fieldControlClass } from "@/components/ui/field-variants";

interface LanguageSwitcherProps {
  /** Sidebar: compact button with dropdown; compact: native select */
  variant?: "sidebar" | "compact";
  className?: string;
}

function useResolvedLanguage(): {
  resolved: SupportedLocale;
  changeLanguage: (code: SupportedLocale) => void;
} {
  const { i18n } = useTranslation("common");
  const resolved = resolveLocale(i18n.resolvedLanguage ?? i18n.language);

  return {
    resolved,
    changeLanguage: (code) => {
      void i18n.changeLanguage(code);
    },
  };
}

export function LanguageSwitcher({
  variant = "sidebar",
  className,
}: LanguageSwitcherProps) {
  if (variant === "compact") {
    return <CompactLanguageSelect className={className} />;
  }

  return <SidebarLanguageMenu className={className} />;
}

function CompactLanguageSelect({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const { resolved, changeLanguage } = useResolvedLanguage();
  const selectId = useId();

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Languages className="h-4 w-4 shrink-0 text-surface-500" />
      <Label htmlFor={selectId} spacing="none" className="sr-only">
        {t("language.label")}
      </Label>
      <select
        id={selectId}
        value={resolved}
        onChange={(e) => {
          changeLanguage(e.target.value as SupportedLocale);
        }}
        className={fieldControlClass({
          size: "sm",
          className: "w-auto pl-2 pr-7",
        })}
      >
        {SUPPORTED_LOCALES.map((code) => (
          <option
            key={code}
            value={code}
          >
            {supportedLanguageLabel(code)}
          </option>
        ))}
      </select>
    </div>
  );
}

const MENU_MIN_SPACE_ABOVE_PX = 220;

function SidebarLanguageMenu({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const { resolved, changeLanguage } = useResolvedLanguage();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<"top" | "bottom">("top");
  const rootRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideAndEscape(open, () => setOpen(false), rootRef);
  const currentLabel = supportedLanguageLabel(resolved);

  const toggle = () => {
    if (!open) {
      // Sidebar footer opens upward; top-of-page usages (auth header) open downward.
      const top = rootRef.current?.getBoundingClientRect().top ?? Infinity;
      setPlacement(top < MENU_MIN_SPACE_ABOVE_PX ? "bottom" : "top");
    }
    setOpen((isOpen) => !isOpen);
  };

  return (
    <div
      ref={rootRef}
      className={cn("relative", className)}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language.label")}
        onClick={toggle}
        className={cn(
          "flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium",
          "transition-colors duration-150",
          open
            ? "bg-surface-100 text-surface-900"
            : "text-surface-600 hover:bg-surface-100/70 hover:text-surface-900",
        )}
      >
        <Languages
          className="h-4 w-4 shrink-0 text-surface-400"
          aria-hidden
        />
        <span>{currentLabel}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-surface-400 transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label={t("language.label")}
          className={cn(
            "absolute z-20 min-w-[11rem] animate-fade-in space-y-px overflow-hidden rounded-lg border border-surface-200 bg-surface-0 p-1 shadow-popover",
            placement === "top"
              ? "bottom-full left-0 mb-1.5"
              : "right-0 top-full mt-1.5",
          )}
        >
          {SUPPORTED_LOCALES.map((code) => {
            const selected = code === resolved;
            return (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={selected}
                className={cn(
                  "flex h-8 w-full items-center justify-between gap-2 rounded-md px-2 text-left text-[13px] transition-colors",
                  selected
                    ? "bg-surface-100 font-medium text-surface-900"
                    : "text-surface-700 hover:bg-surface-100 hover:text-surface-900",
                )}
                onClick={() => {
                  changeLanguage(code);
                  setOpen(false);
                }}
              >
                {supportedLanguageLabel(code)}
                {selected ? (
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-primary-600"
                    aria-hidden
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
